import { api, Recipe, ConsumptionEntry, ConsumptionAggregate, MealType, AmountUnit, UserTarget, DailyAggregate, ApiError, Workout, WorkoutInput, effectiveTargetFor, escapeHtml, renderError } from "../api.js";

type Mode = "day" | "week";

const CHART_DAYS = 30;

export async function renderConsumption (root: HTMLElement): Promise<void> {
    let mode: Mode = "day";

    // Tagesziele einmal pro View-Render laden; null = nicht gesetzt.
    let target: UserTarget | null = null;
    try {
        target = await api.getTarget ();
    }
    catch (err) {
        if (!(err instanceof ApiError) || err.status !== 404) {
            // andere Fehler ignorieren, ohne Targets weiterarbeiten
        }
    }

    const header = document.createElement ("div");
    header.className = "row";

    const title = document.createElement ("h2");
    title.textContent = "Dashboard";
    title.style.margin = "0";
    title.style.flex = "1 1 auto";

    const modeDay = document.createElement ("sl-button");
    modeDay.setAttribute ("size", "small");
    modeDay.setAttribute ("variant", "primary");
    modeDay.textContent = "Tag";

    const modeWeek = document.createElement ("sl-button");
    modeWeek.setAttribute ("size", "small");
    modeWeek.setAttribute ("variant", "default");
    modeWeek.textContent = "Woche";

    const prevDay = document.createElement ("sl-icon-button");
    prevDay.setAttribute ("name", "chevron-left");
    prevDay.setAttribute ("label", "Tag zurueck");
    prevDay.style.flex = "0 0 auto";

    const dayInput = document.createElement ("sl-input");
    dayInput.setAttribute ("type", "date");
    dayInput.setAttribute ("size", "small");
    dayInput.setAttribute ("value", toIsoDate (new Date ()));
    dayInput.style.flex = "0 0 auto";

    const nextDay = document.createElement ("sl-icon-button");
    nextDay.setAttribute ("name", "chevron-right");
    nextDay.setAttribute ("label", "Tag vor");
    nextDay.style.flex = "0 0 auto";

    const todayBtn = document.createElement ("sl-button");
    todayBtn.setAttribute ("size", "small");
    todayBtn.setAttribute ("variant", "text");
    todayBtn.textContent = "Heute";

    const copyFromYesterdayBtn = document.createElement ("sl-button");
    copyFromYesterdayBtn.setAttribute ("size", "small");
    copyFromYesterdayBtn.setAttribute ("variant", "text");
    copyFromYesterdayBtn.textContent = "Wie gestern";

    const templatesBtn = document.createElement ("sl-button");
    templatesBtn.setAttribute ("size", "small");
    templatesBtn.setAttribute ("variant", "text");
    templatesBtn.textContent = "Vorlagen";

    const addBtn = document.createElement ("sl-button");
    addBtn.setAttribute ("variant", "primary");
    addBtn.setAttribute ("size", "small");
    addBtn.textContent = "+ Eintrag";

    header.appendChild (title);
    header.appendChild (modeDay);
    header.appendChild (modeWeek);
    header.appendChild (prevDay);
    header.appendChild (dayInput);
    header.appendChild (nextDay);
    header.appendChild (todayBtn);
    header.appendChild (copyFromYesterdayBtn);
    header.appendChild (templatesBtn);
    header.appendChild (addBtn);

    templatesBtn.addEventListener ("click", () => { void openTemplatesDialog (); });

    async function openTemplatesDialog (): Promise<void> {
        const dlg = document.createElement ("sl-dialog");
        dlg.setAttribute ("label", "Mahlzeit-Vorlagen");
        document.body.appendChild (dlg);
        dlg.addEventListener ("sl-after-hide", () => { window.setTimeout (() => dlg.remove (), 400); });

        const wrap = document.createElement ("div");
        wrap.className = "stack";
        wrap.innerHTML = "<sl-spinner></sl-spinner>";
        dlg.appendChild (wrap);
        (dlg as unknown as { show: () => void }).show ();

        async function reloadList (): Promise<void> {
            wrap.innerHTML = "";
            const list = await api.listMealTemplates ();
            if (list.length === 0) {
                const empty = document.createElement ("div");
                empty.className = "muted";
                empty.textContent = "Noch keine Vorlagen.";
                wrap.appendChild (empty);
            }
            else {
                for (const t of list) {
                    const row = document.createElement ("div");
                    row.className = "row";
                    row.style.borderBottom = "1px solid var(--sl-color-neutral-200, #e5e7eb)";
                    row.style.padding = "0.25rem 0";

                    const lbl = document.createElement ("div");
                    lbl.textContent = t.name;
                    lbl.style.flex = "1 1 auto";

                    const apply = document.createElement ("sl-button");
                    apply.setAttribute ("size", "small");
                    apply.setAttribute ("variant", "primary");
                    apply.textContent = "Anwenden";
                    apply.addEventListener ("click", async () => {
                        apply.setAttribute ("loading", "");
                        try {
                            // Server-Endpoint mit Transaktion: alle Items oder
                            // keiner. Frueher gab es eine Frontend-Schleife,
                            // die bei Fehler in Item N die ersten N-1 Items
                            // bereits im consumption_log liess — halb-appliedes
                            // Template ohne Rollback-Moeglichkeit.
                            await api.applyMealTemplate (t.meal_template_id, {
                                consumed_at: defaultConsumedAtForCurrentView (),
                                meal_type: t.default_meal_type,
                            });
                            (dlg as unknown as { hide: () => void }).hide ();
                            await reload ();
                        }
                        catch (err) {
                            window.alert ((err instanceof Error) ? err.message : "Fehler");
                        }
                        finally {
                            apply.removeAttribute ("loading");
                        }
                    });

                    const del = document.createElement ("sl-icon-button");
                    del.setAttribute ("name", "trash");
                    del.setAttribute ("label", "Loeschen");
                    del.addEventListener ("click", async () => {
                        if (!window.confirm (`Vorlage "${t.name}" loeschen?`)) return;
                        try {
                            await api.deleteMealTemplate (t.meal_template_id);
                            await reloadList ();
                        }
                        catch (err) {
                            window.alert ((err instanceof Error) ? err.message : "Fehler");
                        }
                    });

                    row.appendChild (lbl);
                    row.appendChild (apply);
                    row.appendChild (del);
                    wrap.appendChild (row);
                }
            }

            // Bereich zum Erstellen aus aktuellen Tageseintraegen.
            const createSection = document.createElement ("div");
            createSection.style.marginTop = "1rem";
            createSection.style.paddingTop = "0.5rem";
            createSection.style.borderTop = "1px solid var(--sl-color-neutral-200,#e5e7eb)";

            const ch = document.createElement ("strong");
            ch.textContent = "Aus heutigen Eintraegen erstellen";
            createSection.appendChild (ch);

            const nameIn = document.createElement ("sl-input");
            nameIn.setAttribute ("label", "Name der Vorlage");
            nameIn.setAttribute ("placeholder", "z.B. Standard-Fruehstueck");
            nameIn.style.marginTop = "0.5rem";

            const mealSel = document.createElement ("sl-select");
            mealSel.setAttribute ("label", "Mahlzeit-Typ beim Anwenden");
            mealSel.setAttribute ("value", "breakfast");
            mealSel.innerHTML = `
                <sl-option value="breakfast">Fruehstueck</sl-option>
                <sl-option value="lunch">Mittag</sl-option>
                <sl-option value="dinner">Abendessen</sl-option>
                <sl-option value="snack">Snack</sl-option>
                <sl-option value="drink">Getraenk</sl-option>
            `;

            const createBtn = document.createElement ("sl-button");
            createBtn.setAttribute ("variant", "primary");
            createBtn.textContent = "Erstellen aus heute";
            createBtn.addEventListener ("click", async () => {
                const dayStr = (dayInput as unknown as { value: string }).value;
                const today = dayStr === "" ? new Date () : new Date (`${dayStr}T00:00:00`);
                today.setHours (0, 0, 0, 0);
                const tStart = Math.floor (today.getTime () / 1000);
                const tEnd = tStart + 86400;
                const entries = await api.listConsumption (tStart, tEnd);
                if (entries.length === 0) { window.alert ("Heute noch keine Eintraege - eine Vorlage aus nichts ergibt nichts."); return; }
                const name = (nameIn as unknown as { value: string }).value.trim ();
                if (name === "") { window.alert ("Name eintragen."); return; }
                const meal = (mealSel as unknown as { value: string }).value;
                createBtn.setAttribute ("loading", "");
                try {
                    await api.createMealTemplate ({
                        name,
                        default_meal_type: meal as MealType,
                        items: entries.map (e => ({
                            recipe_id: e.recipe_id,
                            amount: e.amount,
                            amount_unit: e.amount_unit,
                        })),
                    });
                    await reloadList ();
                    (nameIn as unknown as { value: string }).value = "";
                }
                catch (err) {
                    window.alert ((err instanceof Error) ? err.message : "Fehler");
                }
                finally {
                    createBtn.removeAttribute ("loading");
                }
            });

            createSection.appendChild (nameIn);
            createSection.appendChild (mealSel);
            createSection.appendChild (createBtn);
            wrap.appendChild (createSection);
        }

        await reloadList ();
    }

    todayBtn.addEventListener ("click", () => {
        (dayInput as unknown as { value: string }).value = toIsoDate (new Date ());
        void reload ();
    });

    copyFromYesterdayBtn.addEventListener ("click", async () => {
        const dayStr = (dayInput as unknown as { value: string }).value;
        const today = dayStr === "" ? new Date () : new Date (`${dayStr}T00:00:00`);
        const yesterday = new Date (today);
        yesterday.setDate (today.getDate () - 1);

        const yStart = Math.floor (yesterday.getTime () / 1000);
        const yEnd = Math.floor (today.getTime () / 1000);

        copyFromYesterdayBtn.setAttribute ("loading", "");
        try {
            const entries = await api.listConsumption (yStart, yEnd);
            if (entries.length === 0) {
                window.alert ("Gestern wurde nichts geloggt.");
                return;
            }
            if (!window.confirm (`${entries.length} Eintrag/Eintraege von gestern uebernehmen?`)) {
                return;
            }
            for (const e of entries) {
                const delta = e.consumed_at - yStart;
                const newAt = Math.floor (today.getTime () / 1000) + delta;
                await api.createConsumption ({
                    recipe_id: e.recipe_id,
                    consumed_at: newAt,
                    amount: e.amount,
                    amount_unit: e.amount_unit,
                    meal_type: e.meal_type,
                    note: e.note ?? undefined,
                });
            }
            await reload ();
        }
        catch (err) {
            window.alert ((err instanceof Error) ? err.message : "Fehler");
        }
        finally {
            copyFromYesterdayBtn.removeAttribute ("loading");
        }
    });

    function shiftDay (delta: number): void {
        const cur = (dayInput as unknown as { value: string }).value;
        const d = cur === "" ? new Date () : new Date (`${cur}T00:00:00`);
        d.setDate (d.getDate () + delta);
        (dayInput as unknown as { value: string }).value = toIsoDate (d);
        void reload ();
    }

    prevDay.addEventListener ("click", () => shiftDay (-1));
    nextDay.addEventListener ("click", () => shiftDay (1));

    // Quick-Add-Zeile: Wasser/Kaffee/Tee als One-Tap-Buttons, sofern die Rezepte
    // ueber 'trackme-cli seed recipes' bereits angelegt sind.
    const quickRow = document.createElement ("div");
    quickRow.className = "row";
    quickRow.style.marginTop = "0.5rem";
    quickRow.style.flexWrap = "wrap";

    const rangeLabel = document.createElement ("div");
    rangeLabel.className = "muted";
    rangeLabel.style.marginTop = "0.5rem";

    const chart = document.createElement ("div");
    chart.style.marginTop = "1rem";

    const totals = document.createElement ("sl-card");
    totals.style.marginTop = "1rem";

    const list = document.createElement ("div");
    list.className = "stack";
    list.style.marginTop = "1rem";

    const activityCard = document.createElement ("sl-card");
    activityCard.style.marginTop = "1rem";

    const sleepCard = document.createElement ("sl-card");
    sleepCard.style.marginTop = "1rem";

    const workoutsCard = document.createElement ("sl-card");
    workoutsCard.style.marginTop = "1rem";

    const bodyCard = document.createElement ("sl-card");
    bodyCard.style.marginTop = "1rem";

    const garminCard = document.createElement ("sl-card");
    garminCard.style.marginTop = "1rem";

    const noteCard = document.createElement ("sl-card");
    noteCard.style.marginTop = "1rem";

    const dialog = document.createElement ("sl-dialog");
    dialog.setAttribute ("label", "Eintrag hinzufuegen");

    root.appendChild (header);
    root.appendChild (quickRow);
    root.appendChild (rangeLabel);
    root.appendChild (chart);
    root.appendChild (totals);
    root.appendChild (activityCard);
    root.appendChild (sleepCard);
    root.appendChild (workoutsCard);
    root.appendChild (bodyCard);
    root.appendChild (garminCard);
    root.appendChild (list);
    root.appendChild (noteCard);
    root.appendChild (dialog);

    async function renderActivityAndSleep (isoDate: string, dayAggregateKcal: number): Promise<void> {
        // Aktivität
        activityCard.innerHTML = "";
        try {
            const act = await api.getActivity (isoDate);
            const netto = dayAggregateKcal - act.kcal_burned_total;
            const head = document.createElement ("strong");
            head.textContent = "Aktivität";
            activityCard.appendChild (head);
            const t = document.createElement ("table");
            t.className = "totals";
            t.style.marginTop = "0.5rem";
            t.innerHTML = `
                <tr><td>Schritte</td><td>${act.steps.toLocaleString ("de-DE")}</td></tr>
                <tr><td>Stockwerke</td><td>${act.floors}</td></tr>
                <tr><td>Verbrannt</td><td><strong>${act.kcal_burned_total}</strong> kcal (davon ${act.kcal_burned_active} aktiv)</td></tr>
                <tr><td>Netto</td><td><strong style="color:${netto > 0 ? "var(--sl-color-warning-600)" : "var(--sl-color-success-600)"}">${netto >= 0 ? "+" : ""}${netto}</strong> kcal</td></tr>
            `;
            activityCard.appendChild (t);
            activityCard.style.display = "block";
        }
        catch {
            activityCard.style.display = "none";
        }

        // Schlaf - "letzte Nacht" = Schlaf-Eintrag mit dem aktuellen Datum
        sleepCard.innerHTML = "";
        try {
            const slp = await api.getSleep (isoDate);
            const hh = (s: number) => `${Math.floor (s / 3600)}:${String (Math.floor ((s % 3600) / 60)).padStart (2, "0")}`;
            const head = document.createElement ("strong");
            head.textContent = "Letzte Nacht";
            sleepCard.appendChild (head);

            // Stacked bar
            const total = Math.max (1, slp.total_seconds + slp.awake_seconds);
            const segs: Array<[string, number, string]> = [
                ["Tief",  slp.deep_seconds,  "var(--sl-color-primary-700,#1d4ed8)"],
                ["REM",   slp.rem_seconds,   "var(--sl-color-primary-500,#3b82f6)"],
                ["Leicht", slp.light_seconds,"var(--sl-color-primary-300,#93c5fd)"],
                ["Wach",  slp.awake_seconds, "var(--sl-color-neutral-400,#9ca3af)"],
            ];
            let svg = `<svg viewBox="0 0 100 8" preserveAspectRatio="none" style="width:100%;height:14px;border-radius:3px;margin-top:0.5rem;">`;
            let x = 0;
            for (const [, s, c] of segs) {
                const w = (s / total) * 100;
                svg += `<rect x="${x.toFixed (2)}" y="0" width="${w.toFixed (2)}" height="8" fill="${c}"/>`;
                x += w;
            }
            svg += "</svg>";
            const bar = document.createElement ("div");
            bar.innerHTML = svg;
            sleepCard.appendChild (bar);

            const legend = document.createElement ("div");
            legend.className = "muted";
            legend.style.fontSize = "0.75rem";
            legend.style.marginTop = "0.25rem";
            legend.textContent = `Tief ${hh (slp.deep_seconds)} · REM ${hh (slp.rem_seconds)} · Leicht ${hh (slp.light_seconds)} · Wach ${hh (slp.awake_seconds)}`;
            sleepCard.appendChild (legend);

            const total2 = document.createElement ("div");
            total2.style.marginTop = "0.5rem";
            total2.innerHTML = `Gesamt <strong>${hh (slp.total_seconds)}</strong>`
                + (slp.sleep_score !== null ? ` · Score <strong>${slp.sleep_score}</strong>` : "")
                + (slp.start_at > 0 ? ` · ab ${new Date (slp.start_at * 1000).toLocaleTimeString ("de-DE", { hour: "2-digit", minute: "2-digit" })}` : "");
            sleepCard.appendChild (total2);

            sleepCard.style.display = "block";
        }
        catch {
            sleepCard.style.display = "none";
        }
    }

    async function renderWorkouts (isoDate: string): Promise<void> {
        workoutsCard.innerHTML = "";
        try {
            const dayStart = Math.floor (new Date (isoDate + "T00:00:00").getTime () / 1000);
            const dayEnd   = dayStart + 86400;
            const list = await api.listWorkouts (dayStart, dayEnd);

            const headRow = document.createElement ("div");
            headRow.style.display = "flex";
            headRow.style.justifyContent = "space-between";
            headRow.style.alignItems = "center";
            const head = document.createElement ("strong");
            head.textContent = "Workouts";
            headRow.appendChild (head);

            const addBtn2 = document.createElement ("sl-button");
            addBtn2.setAttribute ("size", "small");
            addBtn2.setAttribute ("variant", "default");
            addBtn2.textContent = "+ Manuell";
            addBtn2.addEventListener ("click", () => {
                void openWorkoutDialog ({ kind: "create", defaultStartedAt: defaultConsumedAtForCurrentView () });
            });
            headRow.appendChild (addBtn2);
            workoutsCard.appendChild (headRow);

            if (list.length === 0) {
                const empty = document.createElement ("div");
                empty.className = "muted";
                empty.style.marginTop = "0.5rem";
                empty.style.fontSize = "0.85rem";
                empty.textContent = "Keine Workouts an diesem Tag.";
                workoutsCard.appendChild (empty);
                workoutsCard.style.display = "block";
                return;
            }

            const tbl = document.createElement ("table");
            tbl.className = "totals";
            tbl.style.marginTop = "0.5rem";
            for (const w of list) {
                const tr = document.createElement ("tr");
                const tdLabel = document.createElement ("td");
                const time = new Date (w.started_at * 1000)
                    .toLocaleTimeString ("de-DE", { hour: "2-digit", minute: "2-digit" });
                const typeIcon = workoutEmoji (w.activity_type);
                const nameText = w.name !== "" ? w.name : workoutTypeLabel (w.activity_type);
                tdLabel.innerHTML = `<span style="margin-right:0.25rem">${typeIcon}</span>`
                    + `<strong>${escapeHtml (nameText)}</strong> `
                    + `<span class="muted" style="font-size:0.75rem">${time}</span>`;
                const tdMetrics = document.createElement ("td");
                const parts: string[] = [];
                parts.push (formatDuration (w.duration_seconds));
                if (w.distance_m !== null && w.distance_m > 0) parts.push ((w.distance_m / 1000).toFixed (2) + " km");
                if (w.kcal > 0) parts.push (w.kcal + " kcal");
                if (w.avg_hr !== null && w.avg_hr > 0) parts.push (w.avg_hr + " bpm");
                tdMetrics.style.textAlign = "right";
                tdMetrics.innerHTML = parts.join (" · ");
                if (w.source === "manual") {
                    const edit = document.createElement ("sl-icon-button");
                    edit.setAttribute ("name", "pencil");
                    edit.setAttribute ("label", "Bearbeiten");
                    edit.style.marginLeft = "0.25rem";
                    edit.addEventListener ("click", () => {
                        void openWorkoutDialog ({ kind: "edit", workout: w });
                    });
                    const del = document.createElement ("sl-icon-button");
                    del.setAttribute ("name", "trash");
                    del.setAttribute ("label", "Loeschen");
                    del.addEventListener ("click", async () => {
                        if (!window.confirm (`Workout "${nameText}" loeschen?`)) return;
                        try {
                            await api.deleteWorkout (w.workout_log_id);
                            await renderWorkouts (isoDate);
                        }
                        catch (err) {
                            alert ((err instanceof Error) ? err.message : "Fehler");
                        }
                    });
                    tdMetrics.appendChild (edit);
                    tdMetrics.appendChild (del);
                }
                tr.appendChild (tdLabel);
                tr.appendChild (tdMetrics);
                tbl.appendChild (tr);
            }
            workoutsCard.appendChild (tbl);
            workoutsCard.style.display = "block";
        }
        catch {
            workoutsCard.style.display = "none";
        }
    }

    type WorkoutDialogMode =
        | { kind: "create"; defaultStartedAt: number }
        | { kind: "edit"; workout: Workout };

    async function openWorkoutDialog (m: WorkoutDialogMode): Promise<void> {
        const dlg = document.createElement ("sl-dialog");
        dlg.setAttribute ("label", m.kind === "edit" ? "Workout bearbeiten" : "Workout manuell anlegen");

        const initialEpoch = m.kind === "edit" ? m.workout.started_at : m.defaultStartedAt;
        const initialDate = new Date (initialEpoch * 1000);
        const isoDay = toIsoDate (initialDate);
        const hh = String (initialDate.getHours ()).padStart (2, "0");
        const mm = String (initialDate.getMinutes ()).padStart (2, "0");

        const stack = document.createElement ("div");
        stack.className = "stack";

        const typeSel = document.createElement ("sl-select");
        typeSel.setAttribute ("label", "Typ");
        typeSel.setAttribute ("value", m.kind === "edit" ? m.workout.activity_type : "running");
        typeSel.innerHTML = `
            <sl-option value="running">Laufen</sl-option>
            <sl-option value="cycling">Radfahren</sl-option>
            <sl-option value="swimming">Schwimmen</sl-option>
            <sl-option value="strength_training">Kraft</sl-option>
            <sl-option value="yoga">Yoga</sl-option>
            <sl-option value="walking">Gehen</sl-option>
            <sl-option value="hiking">Wandern</sl-option>
            <sl-option value="other">Sonstiges</sl-option>
        `;

        const nameIn = document.createElement ("sl-input");
        nameIn.setAttribute ("label", "Name (optional)");
        if (m.kind === "edit") nameIn.setAttribute ("value", m.workout.name);

        const dateIn = document.createElement ("sl-input");
        dateIn.setAttribute ("label", "Datum");
        dateIn.setAttribute ("type", "date");
        dateIn.setAttribute ("value", isoDay);
        const timeIn = document.createElement ("sl-input");
        timeIn.setAttribute ("label", "Uhrzeit");
        timeIn.setAttribute ("type", "time");
        timeIn.setAttribute ("value", `${hh}:${mm}`);
        const dtRow = document.createElement ("div");
        dtRow.style.display = "flex";
        dtRow.style.gap = "0.5rem";
        dtRow.appendChild (dateIn);
        dtRow.appendChild (timeIn);

        const durIn = document.createElement ("sl-input");
        durIn.setAttribute ("label", "Dauer (Minuten)");
        durIn.setAttribute ("type", "number");
        durIn.setAttribute ("value",
            String (m.kind === "edit" ? Math.round (m.workout.duration_seconds / 60) : 30));

        const distIn = document.createElement ("sl-input");
        distIn.setAttribute ("label", "Distanz (km, optional)");
        distIn.setAttribute ("type", "number");
        distIn.setAttribute ("step", "0.01");
        if (m.kind === "edit" && m.workout.distance_m !== null) {
            distIn.setAttribute ("value", (m.workout.distance_m / 1000).toFixed (2));
        }

        const kcalIn = document.createElement ("sl-input");
        kcalIn.setAttribute ("label", "kcal (optional)");
        kcalIn.setAttribute ("type", "number");
        if (m.kind === "edit" && m.workout.kcal > 0) {
            kcalIn.setAttribute ("value", String (m.workout.kcal));
        }

        const hrIn = document.createElement ("sl-input");
        hrIn.setAttribute ("label", "Durchschnitts-HR (bpm, optional)");
        hrIn.setAttribute ("type", "number");
        if (m.kind === "edit" && m.workout.avg_hr !== null) {
            hrIn.setAttribute ("value", String (m.workout.avg_hr));
        }

        const noteIn = document.createElement ("sl-input");
        noteIn.setAttribute ("label", "Notiz (optional)");
        if (m.kind === "edit" && m.workout.note !== null) {
            noteIn.setAttribute ("value", m.workout.note);
        }

        const err = document.createElement ("div");
        err.className = "error";

        const submit = document.createElement ("sl-button");
        submit.setAttribute ("variant", "primary");
        submit.textContent = m.kind === "edit" ? "Speichern" : "Anlegen";
        submit.addEventListener ("click", async () => {
            const val = (el: HTMLElement): string => (el as unknown as { value: string }).value;

            const dStr = val (dateIn);
            const tStr = val (timeIn) || "12:00";
            let startedAt = initialEpoch;
            if (dStr !== "") {
                const combined = new Date (`${dStr}T${tStr.length === 5 ? tStr + ":00" : tStr}`);
                if (!Number.isNaN (combined.getTime ())) startedAt = Math.floor (combined.getTime () / 1000);
            }

            const durMin = parseFloat (val (durIn));
            if (Number.isNaN (durMin) || durMin <= 0) { err.textContent = "Dauer angeben"; return; }

            const distKm = parseFloat (val (distIn));
            const kcalN  = parseInt (val (kcalIn), 10);
            const hrN    = parseInt (val (hrIn), 10);

            const payload: WorkoutInput = {
                started_at: startedAt,
                activity_type: val (typeSel),
                name: val (nameIn).trim (),
                duration_seconds: Math.round (durMin * 60),
                kcal: Number.isNaN (kcalN) ? 0 : kcalN,
                distance_m: Number.isNaN (distKm) ? null : Math.round (distKm * 1000),
                avg_hr: Number.isNaN (hrN) ? null : hrN,
                note: val (noteIn).trim () || null,
            };

            submit.setAttribute ("loading", "");
            err.textContent = "";
            try {
                if (m.kind === "edit") {
                    await api.updateWorkout (m.workout.workout_log_id, payload);
                }
                else {
                    await api.createWorkout (payload);
                }
                (dlg as unknown as { hide: () => void }).hide ();
                const dayStr = (dayInput as unknown as { value: string }).value;
                await renderWorkouts (dayStr);
            }
            catch (e) {
                err.textContent = (e instanceof Error) ? e.message : "Fehler";
            }
            finally {
                submit.removeAttribute ("loading");
            }
        });

        [typeSel, nameIn, dtRow, durIn, distIn, kcalIn, hrIn, noteIn, err, submit]
            .forEach (el => stack.appendChild (el));
        dlg.appendChild (stack);
        document.body.appendChild (dlg);
        dlg.addEventListener ("sl-after-hide", () => dlg.remove ());
        (dlg as unknown as { show: () => void }).show ();
    }

    async function renderBody (isoDate: string): Promise<void> {
        bodyCard.innerHTML = "";
        try {
            // Letzte Wiegung bis einschliesslich isoDate und ~7-Tage-Vergleich.
            const toEpoch = Math.floor (new Date (isoDate + "T23:59:59").getTime () / 1000);
            const fromEpoch = toEpoch - 30 * 86400;
            const weights = await api.listWeight (fromEpoch, toEpoch);
            if (weights.length === 0) { bodyCard.style.display = "none"; return; }
            const sorted = [...weights].sort ((a, b) => a.weighed_at - b.weighed_at);
            const latest = sorted[sorted.length - 1];
            const refTime = latest.weighed_at - 7 * 86400;
            let ref = sorted[0];
            for (const w of sorted) {
                if (w.weighed_at <= refTime) ref = w;
            }
            const delta = latest.weight_kg - ref.weight_kg;
            const sign = delta > 0 ? "+" : "";
            const color = Math.abs (delta) < 0.05
                ? "var(--sl-color-neutral-600)"
                : (delta < 0 ? "var(--sl-color-success-600)" : "var(--sl-color-warning-600)");
            const head = document.createElement ("strong");
            head.textContent = "Körper";
            bodyCard.appendChild (head);
            const tbl = document.createElement ("table");
            tbl.className = "totals";
            tbl.style.marginTop = "0.5rem";
            const dateStr = new Date (latest.weighed_at * 1000).toLocaleDateString ("de-DE");
            const extras: string[] = [];
            if (latest.body_fat_pct !== null) extras.push (`KFA ${latest.body_fat_pct.toFixed (1)} %`);
            if (latest.muscle_mass_kg !== null) extras.push (`Muskel ${latest.muscle_mass_kg.toFixed (1)} kg`);
            if (latest.body_water_pct !== null) extras.push (`Wasser ${latest.body_water_pct.toFixed (1)} %`);
            if (latest.bmi !== null) extras.push (`BMI ${latest.bmi.toFixed (1)}`);
            tbl.innerHTML = `
                <tr><td>Gewicht</td><td><strong>${latest.weight_kg.toFixed (1)} kg</strong> <span class="muted">(${dateStr})</span></td></tr>
                <tr><td>Δ 7 Tage</td><td><strong style="color:${color}">${sign}${delta.toFixed (1)} kg</strong></td></tr>
            `;
            bodyCard.appendChild (tbl);
            if (extras.length > 0) {
                const sub = document.createElement ("div");
                sub.className = "muted";
                sub.style.fontSize = "0.75rem";
                sub.style.marginTop = "0.25rem";
                sub.textContent = extras.join (" · ");
                bodyCard.appendChild (sub);
            }
            bodyCard.style.display = "block";
        }
        catch {
            bodyCard.style.display = "none";
        }
    }

    let garminPolling = false;
    async function pollGarminUntilDone (initialLastSyncAt: number | null): Promise<void> {
        if (garminPolling) return;
        garminPolling = true;
        // Backoff: 3 s, 5 s, 8 s, 13 s, dann bei 15 s gedeckelt. Insgesamt
        // ~120 s Budget — Sync ist normal nach 10-30 s durch, ein Initial-
        // Login-Refresh kann laenger brauchen. Wenn der Tab in den Hintergrund
        // geht, browser-throttle wir uns selbst auf 30 s (sonst wuerde der
        // Browser sowieso auf >= 1 min drosseln, und das wirkt dann wie
        // "Polling kaputt", wenn der User zurueckkommt).
        const startedAt = Date.now ();
        const budgetMs  = 120_000;
        let delay = 3000;
        while (Date.now () - startedAt < budgetMs) {
            const wait = document.hidden ? Math.max (delay, 30_000) : delay;
            await new Promise (r => setTimeout (r, wait));
            try {
                const s = await api.getGarminStatus ();
                const changed = (s.last_sync_at !== initialLastSyncAt)
                             || (s.last_sync_error !== null);
                if (changed) {
                    garminPolling = false;
                    await renderGarmin ();
                    return;
                }
            }
            catch { /* einfach weiter pollen */ }
            // Backoff-Folge 3 -> 5 -> 8 -> 13 -> 15 (Fibonacci-aehnlich, geclamped).
            delay = Math.min (15_000, Math.round (delay * 1.6));
        }
        garminPolling = false;
        await renderGarmin ();
    }

    async function renderGarmin (): Promise<void> {
        garminCard.innerHTML = "";
        try {
            const status = await api.getGarminStatus ();
            if (!status.configured) { garminCard.style.display = "none"; return; }

            const headRow = document.createElement ("div");
            headRow.style.display = "flex";
            headRow.style.justifyContent = "space-between";
            headRow.style.alignItems = "center";
            const head = document.createElement ("strong");
            head.textContent = "Garmin-Sync";
            headRow.appendChild (head);
            const syncBtn = document.createElement ("sl-button");
            syncBtn.setAttribute ("size", "small");
            syncBtn.setAttribute ("variant", "default");
            syncBtn.textContent = garminPolling ? "Sync läuft…" : "Jetzt synchronisieren";
            if (garminPolling) syncBtn.setAttribute ("loading", "");
            syncBtn.addEventListener ("click", async () => {
                if (garminPolling) return;
                syncBtn.setAttribute ("loading", "");
                syncBtn.textContent = "Sync läuft…";
                const before = status.last_sync_at;
                try {
                    await api.triggerGarminSync ();
                    void pollGarminUntilDone (before);
                }
                catch (err) {
                    syncBtn.removeAttribute ("loading");
                    syncBtn.textContent = "Jetzt synchronisieren";
                    alert ((err instanceof Error) ? err.message : "Sync fehlgeschlagen");
                }
            });
            headRow.appendChild (syncBtn);
            garminCard.appendChild (headRow);

            const line = document.createElement ("div");
            line.style.marginTop = "0.5rem";
            line.style.fontSize = "0.85rem";
            // last_sync_error enthaelt im Fehlerfall Garmin-API-Response-Snippets
            // (siehe garmin.cpp) — daher muessen wir den String escapen, sonst
            // koennte ein Garmin-MITM- oder Fehler-Response HTML einschleusen.
            const badge = document.createElement ("sl-badge");
            badge.setAttribute ("pill", "");
            const msg = document.createElement ("span");
            const fmtStamp = (epoch: number): string => {
                const dt = new Date (epoch * 1000);
                return dt.toLocaleDateString ("de-DE") + " "
                     + dt.toLocaleTimeString ("de-DE", { hour: "2-digit", minute: "2-digit" });
            };
            if (status.last_sync_error !== null && status.last_sync_error !== "") {
                // Fehler-Modus: zeige beides — wann's gefailt hat UND wann der
                // letzte erfolgreiche Sync war (Stempel der `last_successful_sync_at`).
                badge.setAttribute ("variant", "danger");
                badge.textContent = "Fehler";
                msg.style.color = "var(--sl-color-danger-700)";
                let txt = " " + status.last_sync_error;
                if (status.last_successful_sync_at !== null) {
                    txt += ` (letzter Erfolg: ${fmtStamp (status.last_successful_sync_at)})`;
                }
                msg.textContent = txt;
            }
            else if (status.last_sync_at !== null) {
                badge.setAttribute ("variant", "success");
                badge.textContent = "OK";
                msg.className = "muted";
                msg.textContent = ` letzter Sync ${fmtStamp (status.last_sync_at)}`;
            }
            else {
                badge.setAttribute ("variant", "neutral");
                badge.textContent = "—";
                msg.className = "muted";
                msg.textContent = " noch kein Sync gelaufen";
            }
            line.appendChild (badge);
            line.appendChild (msg);
            garminCard.appendChild (line);
            garminCard.style.display = "block";
        }
        catch {
            garminCard.style.display = "none";
        }
    }

    // Daily-Note rendert sich bei reload() neu (kennt das Datum).
    function renderNoteCard (isoDate: string): void {
        noteCard.innerHTML = "";
        const head = document.createElement ("div");
        head.style.display = "flex";
        head.style.justifyContent = "space-between";
        head.style.alignItems = "center";
        const title2 = document.createElement ("strong");
        title2.textContent = "Notiz zum Tag";
        const status = document.createElement ("span");
        status.className = "muted";
        status.style.fontSize = "0.75rem";
        head.appendChild (title2);
        head.appendChild (status);

        const area = document.createElement ("sl-textarea");
        area.setAttribute ("rows", "2");
        area.setAttribute ("placeholder", "Frei eingeben. Wird beim Verlassen gespeichert.");
        area.style.marginTop = "0.5rem";

        api.getDailyNote (isoDate).then (n => {
            (area as unknown as { value: string }).value = n.note;
        }).catch (() => { /* leer lassen */ });

        let saveTimer: number | undefined;
        const save = async () => {
            const v = (area as unknown as { value: string }).value;
            status.textContent = "speichere ...";
            try {
                await api.putDailyNote (isoDate, v);
                status.textContent = "gespeichert";
                window.setTimeout (() => { status.textContent = ""; }, 1500);
            }
            catch {
                status.textContent = "Fehler";
            }
        };
        area.addEventListener ("sl-blur", () => { void save (); });
        area.addEventListener ("sl-input", () => {
            if (saveTimer !== undefined) window.clearTimeout (saveTimer);
            saveTimer = window.setTimeout (() => { void save (); }, 2000);
        });

        noteCard.appendChild (head);
        noteCard.appendChild (area);
    }

    // Quick-Add nach erstem Load aufbauen (braucht Recipe-Liste fuer Lookup).
    async function buildQuickRow (): Promise<void> {
        quickRow.innerHTML = "";
        let drinks: Recipe[];
        try {
            drinks = await api.listRecipes ("drink");
        }
        catch {
            return;
        }
        const targets: Array<[string, number]> = [
            ["Wasser", 250],
            ["Kaffee schwarz", 200],
            ["Tee (ungesuesst)", 200],
        ];
        for (const [name, amount] of targets) {
            const r = drinks.find (d => d.name === name);
            if (r === undefined) continue;
            const btn = document.createElement ("sl-button");
            btn.setAttribute ("size", "small");
            btn.textContent = `+ ${amount} ml ${r.name}`;
            btn.addEventListener ("click", async () => {
                btn.setAttribute ("loading", "");
                try {
                    await api.createConsumption ({
                        recipe_id: r.recipe_id,
                        amount,
                        amount_unit: r.reference_unit,
                        meal_type: "drink",
                    });
                    await reload ();
                }
                catch (err) {
                    window.alert ((err instanceof Error) ? err.message : "Fehler");
                }
                finally {
                    btn.removeAttribute ("loading");
                }
            });
            quickRow.appendChild (btn);
        }
    }
    void buildQuickRow ();

    function setMode (m: Mode): void {
        mode = m;
        modeDay.setAttribute ("variant", m === "day" ? "primary" : "default");
        modeWeek.setAttribute ("variant", m === "week" ? "primary" : "default");
        void reload ();
    }

    modeDay.addEventListener ("click", () => setMode ("day"));
    modeWeek.addEventListener ("click", () => setMode ("week"));

    async function reload (): Promise<void> {
        const dayStr = (dayInput as unknown as { value: string }).value;
        const baseDate = dayStr === "" ? new Date () : new Date (`${dayStr}T00:00:00`);

        let fromDate: Date;
        let toDate: Date;

        if (mode === "week") {
            const dow = (baseDate.getDay () + 6) % 7;   // Mo = 0
            fromDate = new Date (baseDate);
            fromDate.setDate (baseDate.getDate () - dow);
            fromDate.setHours (0, 0, 0, 0);
            toDate = new Date (fromDate);
            toDate.setDate (fromDate.getDate () + 7);
            rangeLabel.textContent = `KW ${isoWeek (fromDate)} — ${formatDmY (fromDate)} bis ${formatDmY (new Date (toDate.getTime () - 1))}`;
            noteCard.style.display = "none";
        }
        else {
            fromDate = new Date (baseDate);
            fromDate.setHours (0, 0, 0, 0);
            toDate = new Date (fromDate);
            toDate.setDate (fromDate.getDate () + 1);
            rangeLabel.textContent = formatDmY (fromDate);
            noteCard.style.display = "block";
            renderNoteCard (toIsoDate (fromDate));
        }

        const from = Math.floor (fromDate.getTime () / 1000);
        const to = Math.floor (toDate.getTime () / 1000);

        // 30-Tage-Trend endet inklusive des betrachteten Tages.
        const chartEnd = new Date (toDate);
        const chartStart = new Date (toDate);
        chartStart.setDate (chartStart.getDate () - CHART_DAYS);
        const chartFrom = Math.floor (chartStart.getTime () / 1000);
        const chartTo = Math.floor (chartEnd.getTime () / 1000);

        chart.innerHTML = "<sl-spinner></sl-spinner>";
        totals.innerHTML = "<sl-spinner></sl-spinner>";
        list.innerHTML = "";

        try {
            const [agg, entries, byDay] = await Promise.all ([
                api.aggregateConsumption (from, to),
                api.listConsumption (from, to),
                api.consumptionByDay (chartFrom, chartTo),
            ]);

            chart.innerHTML = "";
            chart.appendChild (renderChart (chartStart, CHART_DAYS, byDay, target,
                (isoDay) => {
                    (dayInput as unknown as { value: string }).value = isoDay;
                    if (mode === "week") {
                        // Aus Wochenmodus heraus: in Tagesmodus zum geklickten Tag
                        mode = "day";
                        modeDay.setAttribute ("variant", "primary");
                        modeWeek.setAttribute ("variant", "default");
                    }
                    void reload ();
                }));

            totals.innerHTML = "";
            const dayMultiplier = mode === "week" ? 7 : 1;
            const label = mode === "week" ? "Wochenbilanz" : "Tagesbilanz";
            const head = document.createElement ("strong");
            head.textContent = label;
            totals.appendChild (head);

            const table = document.createElement ("table");
            table.className = "totals";
            table.style.marginTop = "0.5rem";
            table.innerHTML = `
                <tr><td>Kalorien</td><td><strong>${agg.kcal_total}</strong> kcal</td></tr>
                <tr><td>Eiweiss</td><td>${fmt (agg.protein_total)} g</td></tr>
                <tr><td>Fett</td><td>${fmt (agg.fat_total)} g</td></tr>
                <tr><td>Zucker</td><td>${fmt (agg.sugar_total)} g</td></tr>
                <tr><td>Eintraege</td><td>${agg.entry_count}</td></tr>
                <tr><td>Trinkmenge</td><td>${agg.drink_volume_ml_total} ml (${agg.drink_count})</td></tr>
            `;
            totals.appendChild (table);

            const effective = mode === "week" ? target : effectiveTargetFor (target, fromDate);
            if (effective !== null) {
                totals.appendChild (renderProgress (agg, effective, dayMultiplier));
            }

            // Activity + Sleep + Workouts + Body + Garmin-Status nur im Tag-Modus rendern.
            if (mode === "day") {
                void renderActivityAndSleep (toIsoDate (fromDate), agg.kcal_total);
                void renderWorkouts (toIsoDate (fromDate));
                void renderBody (toIsoDate (fromDate));
                void renderGarmin ();
            }
            else {
                activityCard.style.display = "none";
                sleepCard.style.display = "none";
                workoutsCard.style.display = "none";
                bodyCard.style.display = "none";
                garminCard.style.display = "none";
            }

            if (entries.length === 0) {
                const empty = document.createElement ("div");
                empty.className = "muted";
                empty.textContent = mode === "week" ? "Keine Eintraege in dieser Woche." : "Keine Eintraege fuer diesen Tag.";
                list.appendChild (empty);
                return;
            }

            renderEntries (list, entries, mode,
                async (id) => {
                    try {
                        await api.deleteConsumption (id);
                        await reload ();
                    }
                    catch (err) {
                        alert ((err instanceof Error) ? err.message : "Fehler");
                    }
                },
                (entry) => { void openEditDialog (entry); },
            );
        }
        catch (err) {
            chart.innerHTML = "";
            totals.innerHTML = "";
            list.innerHTML = "";
            const x = document.createElement ("div");
            x.className = "error";
            x.textContent = (err instanceof Error) ? err.message : "Fehler beim Laden";
            list.appendChild (x);
        }
    }

    dayInput.addEventListener ("sl-change", () => { void reload (); });

    async function loadRecipesForForm (): Promise<{ recipes: Recipe[]; recentCount: number }> {
        const [recent, foods, drinks] = await Promise.all ([
            api.recentRecipes (),
            api.listRecipes ("food"),
            api.listRecipes ("drink"),
        ]);
        const seen = new Set<number> ();
        const ordered: Recipe[] = [];
        for (const r of recent) {
            if (!seen.has (r.recipe_id)) { seen.add (r.recipe_id); ordered.push (r); }
        }
        for (const r of [...foods, ...drinks]) {
            if (!seen.has (r.recipe_id)) { seen.add (r.recipe_id); ordered.push (r); }
        }
        return { recipes: ordered, recentCount: recent.length };
    }

    // Liefert den Default-Zeitstempel fuer einen neu zu erfassenden Eintrag.
    // Heute -> jetzt; vergangener Tag (Geschaeftsreise-Nachtrag) -> 12:00 UTC
    // dieses Tages, damit der Eintrag im Tagesaggregat des angezeigten Tages
    // landet (Server gruppiert nach UTC-`date(consumed_at)`). Frueher 12:00
    // local — bei extremen Zeitzonen (UTC-12, Pacific) konnte das in den
    // naechsten UTC-Tag rutschen.
    // Der User kann Datum/Uhrzeit im Dialog jederzeit ueberschreiben.
    function defaultConsumedAtForCurrentView (): number {
        const dayStr = (dayInput as unknown as { value: string }).value;
        const today = toIsoDate (new Date ());
        if (dayStr === today || dayStr === "") {
            return Math.floor (Date.now () / 1000);
        }
        const noon = new Date (`${dayStr}T12:00:00Z`);
        return Math.floor (noon.getTime () / 1000);
    }

    addBtn.addEventListener ("click", async () => {
        dialog.setAttribute ("label", "Eintrag hinzufuegen");
        dialog.innerHTML = "<sl-spinner></sl-spinner>";
        (dialog as unknown as { show: () => void }).show ();

        try {
            const { recipes, recentCount } = await loadRecipesForForm ();
            dialog.innerHTML = "";
            dialog.appendChild (entryForm (recipes, recentCount,
                { kind: "create", defaultConsumedAt: defaultConsumedAtForCurrentView () },
                async () => {
                    (dialog as unknown as { hide: () => void }).hide ();
                    await reload ();
                }));
        }
        catch (err) {
            renderError (dialog, err);
        }
    });

    async function openEditDialog (entry: ConsumptionEntry): Promise<void> {
        dialog.setAttribute ("label", `Eintrag bearbeiten — ${entry.recipe_name}`);
        dialog.innerHTML = "<sl-spinner></sl-spinner>";
        (dialog as unknown as { show: () => void }).show ();

        try {
            const { recipes, recentCount } = await loadRecipesForForm ();
            dialog.innerHTML = "";
            dialog.appendChild (entryForm (recipes, recentCount, { kind: "edit", entry }, async () => {
                (dialog as unknown as { hide: () => void }).hide ();
                await reload ();
            }));
        }
        catch (err) {
            renderError (dialog, err);
        }
    }

    await reload ();
}

function renderEntries (
    container: HTMLElement,
    entries: ConsumptionEntry[],
    mode: Mode,
    onDelete: (id: number) => Promise<void>,
    onEdit: (entry: ConsumptionEntry) => void,
): void {
    if (mode === "day") {
        // Im Tag-Modus nach Mahlzeit gruppiert.
        const order: MealType[] = ["breakfast", "lunch", "dinner", "snack", "drink"];
        for (const meal of order) {
            const subset = entries.filter (e => e.meal_type === meal);
            if (subset.length === 0) continue;
            const heading = document.createElement ("h3");
            heading.textContent = mealLabel (meal);
            heading.style.margin = "0.75rem 0 0.25rem 0";
            heading.style.fontSize = "1rem";
            container.appendChild (heading);
            for (const e of subset) {
                container.appendChild (entryCard (e,
                    () => onDelete (e.consumption_log_id),
                    () => onEdit (e)));
            }
        }
        return;
    }

    // Wochenmodus: gruppiert nach Tag, innerhalb des Tages nach Zeit.
    let lastDay = "";
    for (const e of entries) {
        const dayKey = new Date (e.consumed_at * 1000).toISOString ().slice (0, 10);
        if (dayKey !== lastDay) {
            lastDay = dayKey;
            const header = document.createElement ("h3");
            header.textContent = formatDmY (new Date (`${dayKey}T00:00:00`));
            header.style.margin = "0.5rem 0 0.25rem 0";
            container.appendChild (header);
        }
        container.appendChild (entryCard (e,
            () => onDelete (e.consumption_log_id),
            () => onEdit (e)));
    }
}

function entryCard (
    e: ConsumptionEntry,
    onDelete: () => Promise<void>,
    onEdit: () => void,
): HTMLElement {
    const card = document.createElement ("sl-card");

    const row = document.createElement ("div");
    row.className = "row";

    const left = document.createElement ("div");
    left.style.flex = "1 1 auto";

    const name = document.createElement ("strong");
    name.textContent = e.recipe_name;

    const detail = document.createElement ("div");
    detail.className = "muted";
    detail.textContent = `${fmt (e.amount)} ${unitLabel (e.amount_unit)} — ${timeLabel (e.consumed_at)}`;

    left.appendChild (name);
    left.appendChild (detail);

    if (e.note !== null && e.note !== undefined && e.note !== "") {
        const note = document.createElement ("div");
        note.className = "muted";
        note.style.fontStyle = "italic";
        note.textContent = e.note;
        left.appendChild (note);
    }

    const edit = document.createElement ("sl-icon-button");
    edit.setAttribute ("name", "pencil");
    edit.setAttribute ("label", "Bearbeiten");
    edit.style.flex = "0 0 auto";
    edit.addEventListener ("click", () => onEdit ());

    const del = document.createElement ("sl-icon-button");
    del.setAttribute ("name", "trash");
    del.setAttribute ("label", "Loeschen");
    del.style.flex = "0 0 auto";
    del.addEventListener ("click", () => { void onDelete (); });

    row.appendChild (left);
    row.appendChild (edit);
    row.appendChild (del);
    card.appendChild (row);

    return card;
}

type EntryFormMode =
    | { kind: "create"; defaultConsumedAt: number }
    | { kind: "edit"; entry: ConsumptionEntry };

function entryForm (
    recipes: Recipe[],
    recentCount: number,
    mode: EntryFormMode,
    onSaved: () => Promise<void>,
): HTMLElement {
    const isEdit = mode.kind === "edit";
    const existing: ConsumptionEntry | undefined = mode.kind === "edit" ? mode.entry : undefined;
    const initialEpoch = mode.kind === "edit" ? mode.entry.consumed_at : mode.defaultConsumedAt;

    const form = document.createElement ("div");
    form.className = "stack";

    const recipeSelect = document.createElement ("sl-select");
    recipeSelect.setAttribute ("label", "Rezept");
    recipeSelect.setAttribute ("hoist", "");

    const initialRecipe = existing !== undefined
        ? recipes.find (r => r.recipe_id === existing.recipe_id) ?? null
        : (recipes.length > 0 ? recipes[0] : null);

    if (initialRecipe !== null) {
        recipeSelect.setAttribute ("value", String (initialRecipe.recipe_id));
    }

    // Recent-Block oben, durch <sl-divider> getrennt vom Rest.
    const parts: string[] = [];
    for (let i = 0; i < recipes.length; ++i) {
        if (i === recentCount && recentCount > 0 && i < recipes.length) {
            parts.push (`<sl-divider></sl-divider>`);
        }
        parts.push (`<sl-option value="${recipes[i].recipe_id}">${escapeHtml (recipes[i].name)}</sl-option>`);
    }
    recipeSelect.innerHTML = parts.join ("");

    const amount = document.createElement ("sl-input");
    amount.setAttribute ("label", "Menge");
    amount.setAttribute ("type", "number");
    amount.setAttribute ("value", String (existing?.amount ?? initialRecipe?.reference_amount ?? 100));

    const unit = document.createElement ("sl-select");
    unit.setAttribute ("label", "Einheit");
    unit.setAttribute ("value", existing?.amount_unit ?? initialRecipe?.reference_unit ?? "gram");
    unit.innerHTML = `
        <sl-option value="gram">g</sl-option>
        <sl-option value="milliliter">ml</sl-option>
        <sl-option value="piece">Stueck</sl-option>
        <sl-option value="portion">Portion</sl-option>
    `;

    // Beim Wechsel des Rezepts die Einheit *immer* mitziehen — der Tages-
    // Aggregat-Query (consumption_aggregate) filtert auf
    // `cl.amount_unit = r.reference_unit`, sonst faellt der Eintrag still
    // aus dem Tagesaggregat und dem Chart raus.
    //
    // Die Menge dagegen nur dann ueberschreiben, wenn der User sie *nicht*
    // schon manuell angefasst hat — sonst verliert er beim Edit eines
    // Eintrags seinen handgetippten Wert, sobald er das Rezept wechselt.
    let userTouchedAmount = false;
    const onAmountInput = (): void => {
        // Wenn der User das Feld geleert hat, gilt das als "nicht angefasst" —
        // ein nachfolgender Rezeptwechsel darf wieder den Default-Wert setzen.
        const v = (amount as unknown as { value: string }).value;
        userTouchedAmount = v.trim () !== "";
    };
    amount.addEventListener ("sl-input", onAmountInput);
    amount.addEventListener ("input",    onAmountInput);

    recipeSelect.addEventListener ("sl-change", () => {
        const id = parseInt ((recipeSelect as unknown as { value: string }).value, 10);
        const r = recipes.find (x => x.recipe_id === id);
        if (r !== undefined) {
            (unit as unknown as { value: string }).value = r.reference_unit;
            if (!userTouchedAmount) {
                (amount as unknown as { value: string }).value = String (r.reference_amount);
            }
        }
    });

    const meal = document.createElement ("sl-select");
    meal.setAttribute ("label", "Mahlzeit");
    meal.setAttribute ("value", existing?.meal_type ?? defaultMealType ());
    meal.innerHTML = `
        <sl-option value="breakfast">Fruehstueck</sl-option>
        <sl-option value="lunch">Mittag</sl-option>
        <sl-option value="dinner">Abendessen</sl-option>
        <sl-option value="snack">Snack</sl-option>
        <sl-option value="drink">Getraenk</sl-option>
    `;

    // Datum + Uhrzeit. Im Create-Modus auf den aktuell angezeigten Dashboard-Tag
    // vorbelegt (so dass Geschaeftsreise-Nachtraege ohne extra Klick im richtigen
    // Tagesaggregat landen), im Edit-Modus auf den existierenden Zeitstempel.
    // Beide Felder bleiben editierbar.
    const initialDate = new Date (initialEpoch * 1000);
    const isoDay = toIsoDate (initialDate);
    const hh = String (initialDate.getHours ()).padStart (2, "0");
    const mm = String (initialDate.getMinutes ()).padStart (2, "0");

    const dateIn = document.createElement ("sl-input");
    dateIn.setAttribute ("label", "Datum");
    dateIn.setAttribute ("type", "date");
    dateIn.setAttribute ("value", isoDay);

    const timeIn = document.createElement ("sl-input");
    timeIn.setAttribute ("label", "Uhrzeit");
    timeIn.setAttribute ("type", "time");
    timeIn.setAttribute ("value", `${hh}:${mm}`);

    const dateTimeRow = document.createElement ("div");
    dateTimeRow.style.display = "flex";
    dateTimeRow.style.gap = "0.5rem";
    dateTimeRow.appendChild (dateIn);
    dateTimeRow.appendChild (timeIn);

    const note = document.createElement ("sl-input");
    note.setAttribute ("label", "Notiz (optional)");
    if (existing?.note !== null && existing?.note !== undefined) {
        note.setAttribute ("value", existing.note);
    }

    const error = document.createElement ("div");
    error.className = "error";

    const submit = document.createElement ("sl-button");
    submit.setAttribute ("variant", "primary");
    submit.textContent = isEdit ? "Speichern" : "Hinzufuegen";
    submit.addEventListener ("click", async () => {
        const val = (el: HTMLElement): string => (el as unknown as { value: string }).value;
        const recipeId = parseInt (val (recipeSelect), 10);

        if (Number.isNaN (recipeId)) {
            error.textContent = "Rezept waehlen";
            return;
        }

        // Datum + Uhrzeit zu Unix-Epoch zusammensetzen. Wenn leer, faellt das
        // auf den initialEpoch zurueck (verhindert ungewollten Drift auf
        // "jetzt", wenn ein Browser die type=time-Felder verschluckt).
        const dStr = val (dateIn);
        const tStr = val (timeIn) || "12:00";
        let consumedAt = initialEpoch;
        if (dStr !== "") {
            const combined = new Date (`${dStr}T${tStr.length === 5 ? tStr + ":00" : tStr}`);
            if (!Number.isNaN (combined.getTime ())) {
                consumedAt = Math.floor (combined.getTime () / 1000);
            }
        }

        submit.setAttribute ("loading", "");
        error.textContent = "";

        const payload = {
            recipe_id: recipeId,
            consumed_at: consumedAt,
            amount: parseFloat (val (amount)) || 0,
            amount_unit: val (unit) as AmountUnit,
            meal_type: val (meal) as MealType,
            note: val (note).trim () || undefined,
        };

        try {
            if (mode.kind === "edit") {
                await api.updateConsumption (mode.entry.consumption_log_id, payload);
            }
            else {
                await api.createConsumption (payload);
            }
            await onSaved ();
        }
        catch (err) {
            error.textContent = (err instanceof Error) ? err.message : "Fehler";
        }
        finally {
            submit.removeAttribute ("loading");
        }
    });

    [recipeSelect, amount, unit, meal, dateTimeRow, note, error, submit].forEach (el => form.appendChild (el));

    return form;
}

function renderChart (
    startDate: Date,
    days: number,
    byDay: DailyAggregate[],
    target: UserTarget | null,
    onBarClick: (isoDay: string) => void,
): HTMLElement {
    const byDate = new Map<string, DailyAggregate> ();
    for (const r of byDay) {
        byDate.set (r.date, r);
    }

    let maxKcal = target?.kcal_target ?? 1;
    for (const r of byDay) {
        if (r.kcal_total > maxKcal) maxKcal = r.kcal_total;
    }
    if (maxKcal < 1) maxKcal = 1;

    const width = 600;
    const height = 80;
    const barWidth = width / days;
    const padding = 1;

    const targetLineY = target !== null
        ? height - (target.kcal_target / maxKcal) * height
        : null;

    const isoForBar: string[] = [];

    let svg = `<svg viewBox="0 0 ${width} ${height}" preserveAspectRatio="none" `
        + `style="width:100%;height:${height}px;display:block;background:var(--sl-color-neutral-100,#f3f4f6);border-radius:4px;cursor:pointer;">`;

    for (let i = 0; i < days; ++i) {
        const d = new Date (startDate);
        d.setDate (d.getDate () + i);
        const iso = d.toISOString ().slice (0, 10);
        isoForBar.push (iso);
        const rec = byDate.get (iso);
        const kcal = rec?.kcal_total ?? 0;
        const h = (kcal / maxKcal) * height;
        const x = i * barWidth + padding;
        const y = height - h;
        const w = barWidth - 2 * padding;
        const overTarget = target !== null && kcal > target.kcal_target;
        const fill = kcal === 0
            ? "var(--sl-color-neutral-300,#d1d5db)"
            : overTarget
                ? "var(--sl-color-warning-500,#f59e0b)"
                : "var(--sl-color-primary-500,#3b82f6)";
        // Klick-Hitbox: ueber die ganze Breite des Slots, nicht nur den Balken.
        // Damit kann man auch leere Tage anklicken.
        svg += `<rect data-iso="${iso}" x="${(i * barWidth).toFixed (2)}" y="0" `
             + `width="${barWidth.toFixed (2)}" height="${height}" fill="transparent" style="pointer-events:all;">`
             + `<title>${iso}: ${kcal} kcal</title></rect>`;
        svg += `<rect x="${x.toFixed (2)}" y="${y.toFixed (2)}" `
             + `width="${w.toFixed (2)}" height="${h.toFixed (2)}" fill="${fill}" style="pointer-events:none;"/>`;
    }

    if (targetLineY !== null) {
        svg += `<line x1="0" y1="${targetLineY.toFixed (2)}" x2="${width}" y2="${targetLineY.toFixed (2)}" `
             + `stroke="var(--sl-color-success-600,#16a34a)" stroke-width="1" stroke-dasharray="3,3" style="pointer-events:none;"/>`;
    }

    svg += `</svg>`;

    const wrap = document.createElement ("div");
    wrap.innerHTML = svg;

    // Click-Delegation auf den SVG-Container (statt pro <rect>-Listener).
    wrap.addEventListener ("click", (ev) => {
        const tgt = ev.target as Element | null;
        if (tgt === null) return;
        const iso = tgt.getAttribute ("data-iso");
        if (iso !== null) onBarClick (iso);
    });

    const caption = document.createElement ("div");
    caption.className = "muted";
    caption.style.fontSize = "0.75rem";
    caption.style.marginTop = "0.25rem";
    caption.textContent = target !== null
        ? `${days} Tage Kalorien-Trend, gestrichelt = Tagesziel ${target.kcal_target} kcal`
        : `${days} Tage Kalorien-Trend`;
    wrap.appendChild (caption);

    return wrap;
}

function renderProgress (
    agg: ConsumptionAggregate,
    target: UserTarget,
    multiplier: number,
): HTMLElement {
    const wrap = document.createElement ("div");
    wrap.style.marginTop = "0.75rem";

    const rows: Array<[string, number, number, string]> = [
        ["Kalorien", agg.kcal_total, target.kcal_target * multiplier, "kcal"],
        ["Eiweiss", agg.protein_total, target.protein_target * multiplier, "g"],
        ["Fett", agg.fat_total, target.fat_target * multiplier, "g"],
        ["Zucker", agg.sugar_total, target.sugar_target * multiplier, "g"],
    ];

    for (const [label, value, goal, unit] of rows) {
        const goalSafe = goal > 0 ? goal : 1;
        const pct = Math.min (100, Math.round ((value / goalSafe) * 100));
        const over = value > goal;

        const row = document.createElement ("div");
        row.style.margin = "0.4rem 0";

        const head = document.createElement ("div");
        head.style.display = "flex";
        head.style.justifyContent = "space-between";
        head.style.fontSize = "0.875rem";

        const lbl = document.createElement ("span");
        lbl.textContent = label;
        const val = document.createElement ("span");
        val.className = over ? "error" : "muted";
        val.textContent = `${fmt (value)} / ${fmt (goal)} ${unit}`;

        head.appendChild (lbl);
        head.appendChild (val);

        const bar = document.createElement ("sl-progress-bar");
        bar.setAttribute ("value", String (pct));

        if (over) {
            bar.style.setProperty ("--indicator-color", "var(--sl-color-warning-500, #f59e0b)");
        }

        row.appendChild (head);
        row.appendChild (bar);
        wrap.appendChild (row);
    }

    return wrap;
}

function fmt (n: number): string {
    return Number.isInteger (n) ? String (n) : n.toFixed (1);
}

function unitLabel (u: AmountUnit): string {
    switch (u) {
        case "gram":       return "g";
        case "milliliter": return "ml";
        case "piece":      return "Stueck";
        case "portion":    return "Portion";
    }
}

function mealLabel (m: MealType): string {
    switch (m) {
        case "breakfast": return "Fruehstueck";
        case "lunch":     return "Mittag";
        case "dinner":    return "Abendessen";
        case "snack":     return "Snack";
        case "drink":     return "Getraenk";
    }
}

function timeLabel (epoch: number): string {
    return new Date (epoch * 1000).toLocaleTimeString ("de-DE", { hour: "2-digit", minute: "2-digit" });
}

function toIsoDate (d: Date): string {
    // Lokales YYYY-MM-DD — passt zum `<sl-input type="date">`-Format, das auch
    // den lokalen Kalendertag liefert. Frueher `d.toISOString().slice(0,10)`,
    // was UTC zurueckgab → in Berlin nach 23:00 UTC (= 01:00 lokal) lieferte
    // toIsoDate(new Date()) bereits den naechsten Tag, dayInput aber den
    // aktuellen → defaultConsumedAtForCurrentView und shiftDay sprangen
    // unerwartet um einen Tag.
    const y = d.getFullYear ();
    const m = String (d.getMonth () + 1).padStart (2, "0");
    const day = String (d.getDate ()).padStart (2, "0");
    return `${y}-${m}-${day}`;
}

function formatDmY (d: Date): string {
    return d.toLocaleDateString ("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
}

function isoWeek (d: Date): number {
    const t = new Date (Date.UTC (d.getFullYear (), d.getMonth (), d.getDate ()));
    const day = (t.getUTCDay () + 6) % 7;
    t.setUTCDate (t.getUTCDate () - day + 3);
    const firstThursday = new Date (Date.UTC (t.getUTCFullYear (), 0, 4));
    const weekDay = (firstThursday.getUTCDay () + 6) % 7;
    firstThursday.setUTCDate (firstThursday.getUTCDate () - weekDay + 3);
    return 1 + Math.round ((t.getTime () - firstThursday.getTime ()) / (7 * 24 * 60 * 60 * 1000));
}

function defaultMealType (): MealType {
    const h = new Date ().getHours ();
    if (h < 10) return "breakfast";
    if (h < 14) return "lunch";
    if (h < 18) return "snack";
    return "dinner";
}

function formatDuration (seconds: number): string {
    const h = Math.floor (seconds / 3600);
    const m = Math.floor ((seconds % 3600) / 60);
    if (h > 0) return `${h}h ${String (m).padStart (2, "0")}min`;
    return `${m}min`;
}

function workoutTypeLabel (t: string): string {
    const map: Record<string, string> = {
        running: "Laufen", cycling: "Radfahren", swimming: "Schwimmen",
        strength_training: "Kraft", yoga: "Yoga", walking: "Gehen",
        hiking: "Wandern", other: "Workout",
    };
    // Unbekannten typeKey *nicht* echo-en: Garmin liefert neue Typen
    // gelegentlich nach (server-controlled string). Default "Workout" ist
    // sicher und konsistent mit dem Whitelist-Pattern.
    return map[t] ?? "Workout";
}

function workoutEmoji (t: string): string {
    if (t.startsWith ("run"))      return "🏃";
    if (t.startsWith ("cycl"))     return "🚴";
    if (t.startsWith ("swim"))     return "🏊";
    if (t === "strength_training") return "🏋️";
    if (t === "yoga")              return "🧘";
    if (t === "walking")           return "🚶";
    if (t === "hiking")            return "🥾";
    return "⚡";
}
